import BlockUi from '@availity/block-ui';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { DateInput } from 'semantic-ui-calendar-react-17';
import { Button, Dropdown, DropdownItemProps, DropdownProps, Form, Header, Loader, Segment, Tab, TabProps } from 'semantic-ui-react';
import { AgencyStateType } from '../../../../actions/actionTypes';
import { getGtfsTripsAndStops, getShapeData } from '../../../../actions/gtfsStaticActions';
import { getStopHeadways, getStopHeadwaysTrips } from '../../../../actions/otpActions';
import Api from '../../../../api/api';
import { AppState } from '../../../../reducers';
import { getSelectedOrDefaultAgency } from '../../../../selectors';
import { GtfsTripAndStops } from '../../../../types/gtfsTypes';
import { StopArrivalHeadway, StopArrivalHeadwayTrip } from '../../../../types/headwaysTypes';
import { InfoboxProps, PolylineProps, PushpinProps } from '../../../../types/MapModels';
import { DropDownStateType } from '../../../../types/types';
import { IDirectionModel, IRouteModel } from '../../../../types/view-models-interfaces';
import Utils from '../../../../utilities/utils';
import TransitMap from '../../../shared/AzureMap/TransitMap';
import RouteVariantsSelector from '../../../shared/RouteVariantsSelector';
import { createStopInfoInfobox, createStopPushpin } from '../../../shared/StopMapElements';
import StopHeadwaysChart from './StopHeadwaysChart';
import StopHeadwaysTable from './StopHeadwaysTable';


interface Props {
    agency: AgencyStateType | undefined;
}

export interface ScheduleTableData {
    tripId: string;
    tripScheduledStart: Date;
    tripScheduledFinish: Date;
    scheduledTime?: Date;
}

interface SelectionType {
    routeName: string;
    directionName: string;
    directionVariantId: string;
}

export interface HeadwaysChartDataType {
    actualTime: string;
    headwaySec: number;
    sequence: number;
    vehicleId: string;
}

export const initialStopsState: DropDownStateType = {
    options: [],
    selectedValue: '',
};

const styles = {
    mapContainer: {
        height: '730px',
    } as React.CSSProperties,
    routeSelection: {
        height: '674px',
    } as React.CSSProperties,
    tab: {
        border: 'none',
        padding: 0,
        marginBottom: 0,
    } as React.CSSProperties,
    speedMap: {
        paddingBottom: 0,
        maxWidth: '100%',
    } as React.CSSProperties,
    stopsDropdown: {
        width: '100%',
        marginBottom: '10px',
    } as React.CSSProperties,
    layout: {
        display: 'flex',
        flexDirection: 'row',
    } as React.CSSProperties,
    tabContainer: {
        flexShrink: 0,
        //flexBasis: 'fit-content',
    } as React.CSSProperties,
};

const StopHeadways: React.FC<Props> = ({ agency }) => {
    const [formBlockingState, setFormBlockingState] = useState(false);
    const [center, setCenter] = useState<CoordinatePair>();
    const [mapBoundsState, setMapBoundsState] = useState<{ north: number; south: number; east: number; west: number; } | undefined>();
    const [selectedDateState, setSelectedDateState] = useState(moment(new Date().setMinutes(0, 0, 0)).add(-1, 'days').format('YYYY-MM-DD'));
    const [zoom, setZoom] = useState<number | undefined>(undefined);
    const [polylineState, setPolylineState] = useState<PolylineProps>();
    const [pushpinsState, setPushpinsState] = useState<PushpinProps[]>([]);
    const [infoboxesState, setInfoboxesState] = useState<InfoboxProps[]>([]);
    const [stopsState, setStopsState] = useState<GtfsTripAndStops[]>([]);
    const [selectedStopState, setSelectedStopState] = useState<GtfsTripAndStops | undefined>(undefined);
    const [routesListState, setRoutesListState] = useState<IRouteModel[]>([]);
    const [tripsState, setTripsState] = useState<StopArrivalHeadwayTrip[]>([]);
    const [headwaysState, setHeadwaysState] = useState<StopArrivalHeadway[]>([]);
    const [headwaysChartState, setHeadwaysChartState] = useState<HeadwaysChartDataType[]>([]);
    const [selectedItemsState, setSelectedItemsState] = useState<SelectionType | undefined>();
    const [tabIndexState, setTabIndexState] = useState<number>(0);
    const [stopsDropdownState, setStopsDropdownState] = useState(initialStopsState);

    useEffect(() => {
        clearStates();
        if (agency === undefined) return;
        (async (agencyId: string) => {
            const { Latitude, Longitude } = await Api.getOrgLocation(agencyId);
            setCenter([Latitude, Longitude]);
            await retrieveTripsData();
        })(agency.id);
    }, [agency?.id]);

    useEffect(() => {
        setTabIndexState(tripsState.length === 0 ? 0 : 1);
    }, [tripsState]);

    useEffect(() => {
        if (selectedStopState) {
            setStopsDropdownState({
                options: [{ value: selectedStopState.stopId, text: `${selectedStopState.stopName} (${selectedStopState.stopId})` }],
                selectedValue: selectedStopState.stopId,
            });
            (async () => await updateHeadways(selectedStopState.routeShortName, selectedStopState.directionVariantInternalId, selectedStopState.stopId))();
        }
    }, [selectedStopState]);

    const handleSelectedDateChange = async (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string; }) => {
        setSelectedDateState(value);
    };

    const handleRouteVariantChecked = async (route: IRouteModel, direction: IDirectionModel) => {
        routesListState.forEach(r => {
            r.directions.forEach(d => {
                d.selected = r.routeId === route.routeId && r.routeName === route.routeName && d.directionVariantId === direction.directionVariantId;
            });
        });
        setHeadwaysState([]);
        setHeadwaysChartState([]);
        setSelectedStopState(undefined);
        setStopsDropdownState(initialStopsState);
        setSelectedItemsState({
            routeName: route.routeName,
            directionName: direction.cardinalDirection,
            directionVariantId: direction.directionVariantId,
        });
        if (agency && agency.id && direction.shapeId) {
            setFormBlockingState(true);
            try {
                const [trips, shapePoints] = await Promise.all(
                    [getStopHeadwaysTrips(agency.id, selectedDateState, route.routeName, direction.directionVariantId),
                    getShapeData(agency.id, selectedDateState, direction.shapeId)]);
                setPolylineState({
                    locations: shapePoints.map(({ latitude, longitude }) => [latitude, longitude]),
                    options: {
                        strokeColor: 'red',
                        strokeThickness: 3,
                    },
                });
                const lats = shapePoints.map(p => p.latitude);
                const lons = shapePoints.map(p => p.longitude);
                setMapBoundsState({
                    north: Math.max(...lats),
                    south: Math.min(...lats),
                    east: Math.max(...lons),
                    west: Math.min(...lons),
                });
                const stopsForSelectedDirection = stopsState.filter(s => s.directionVariantInternalId === direction.directionVariantId);
                drawPushpins(stopsForSelectedDirection);
                setTripsState(trips);
            } catch {
                setTripsState([]);
                setPushpinsState([]);
                setInfoboxesState([]);
            } finally {
                setFormBlockingState(false);
            }
        }
    };

    const handleTabChange = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>, { activeIndex }: TabProps) => {
        setTabIndexState(activeIndex as number);
    };

    const drawPushpins = async (stopPositions: GtfsTripAndStops[]) => {
        if (stopPositions.length > 0) {
            setPushpinsState(
                stopPositions.map(stop => {
                    const basePushpin = createStopPushpin({
                        location: [stop.latitude, stop.longitude],
                        onClick: async () => {
                            setInfoboxesState(prevState => (
                                prevState.map(i => i.location[0] === stop.latitude && i.location[1] === stop.longitude ?
                                    { ...i, options: { ...i.options, visible: true } } :
                                    { ...i, options: { ...i.options, visible: false } })
                            ));
                            setSelectedStopState(stop);
                        },
                    });

                    return {
                        ...basePushpin,
                        eventHandlers: [
                            ...(basePushpin.eventHandlers || []),
                            {
                                event: 'mouseover' as const,
                                callback: async () => {
                                    setInfoboxesState(prevState => (
                                        prevState.map(i => i.location[0] === stop.latitude && i.location[1] === stop.longitude ?
                                            { ...i, options: { ...i.options, visible: true } } :
                                            { ...i, options: { ...i.options, visible: false } })
                                    ));
                                },
                            },
                        ],
                    };
                }));
            setInfoboxesState(
                stopPositions.map(({ latitude, longitude, stopName, stopId }) => (
                    createStopInfoInfobox({
                        location: [latitude, longitude],
                        label: `${stopName} (${stopId})`,
                        visible: false,
                    })
                )));
        } else {
            setPushpinsState([]);
            setInfoboxesState([]);
        }
    };

    const updateHeadways = async (routeShortName: string, directionVariantInternalId: string, stopId: string) => {
        if (agency && agency.id) {
            setFormBlockingState(true);
            try {
                const headways = await getStopHeadways({ agencyId: agency.id, reportDate: selectedDateState, routeName: routeShortName, directionVariantInternalId, stopId });
                setHeadwaysState(headways);
                const headwaysChartData = Object.entries(Utils.groupBy(headways.sort((a, b) => new Date(a.actualTime!).getTime() - new Date(b.actualTime!).getTime()), h => h.tripId))
                    .flatMap<HeadwaysChartDataType>(([_tripId, tripsData]) => tripsData.sort((a, b) => Number(a.stopSequence) - Number(b.stopSequence)).map<HeadwaysChartDataType>((trip, sequence) => ({
                        actualTime: moment.parseZone(trip.actualTime).format('h:mm A'),
                        headwaySec: Number(trip.headwaySec),
                        vehicleId: trip.vehicleId,
                        sequence,
                    })));
                setHeadwaysChartState(headwaysChartData);
            } catch {
                setHeadwaysState([]);
            }
            finally {
                setFormBlockingState(false);
            }
        }
    };

    const retrieveTripsData = async () => {
        clearStates();
        if (agency && agency.id) {
            setFormBlockingState(true);
            try {
                const stopsData = await getGtfsTripsAndStops(agency.id, selectedDateState);
                setStopsState(stopsData);
                updateRoutesList(stopsData);
            } catch {
                setStopsState([]);
            } finally {
                setFormBlockingState(false);
            }
        } else {
            setStopsState([]);
        }
    };
    const updateRoutesList = (stopsData: GtfsTripAndStops[]) => {
        const routesList = Object.entries(Utils.groupBy(stopsData, t => t.routeShortName))
            .map<IRouteModel>(([routeName, routeData]) => ({
                routeId: routeName,
                routeName: routeName,
                cssColor: 'rgb(0, 64, 128)',
                directions: Object.entries(Utils.groupBy(routeData, r => r.directionVariantInternalId)).map<IDirectionModel>(([directionVariantId, variantData]) => ({
                    directionVariantId,
                    distance: 0,
                    selected: false,
                    cardinalDirection: variantData[0].otsTripShortName,
                    cardinalDirectionShort: variantData[0].otsTripShortName,
                    fromStopName: variantData.reduce((prev: GtfsTripAndStops, curr: GtfsTripAndStops) => prev.stopSequence < curr.stopSequence ? prev : curr).stopName,
                    toStopName: variantData.reduce((prev: GtfsTripAndStops, curr: GtfsTripAndStops) => prev.stopSequence > curr.stopSequence ? prev : curr).stopName,
                    isMain: true,
                    shapeId: variantData[0].shapeId,
                })),
            }))
            .sort((a, b) => (a.routeName > b.routeName) as unknown as number - ((a.routeName < b.routeName) as unknown as number));
        setRoutesListState(routesList);
    };

    let foundOptions: DropdownItemProps[] = stopsDropdownState.options;
    const handleStopChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value: stopId }: DropdownProps) => {
        if (!stopId)
            foundOptions = [];
        setStopsDropdownState({
            options: foundOptions,
            selectedValue: stopId as string,
        });
        const selectedStopData = stopsState.find(s => s.stopId === stopId);
        setSelectedStopState(selectedStopData);
        if (selectedStopData)
            navigateToStop(selectedStopData);
    };
    const stopsSearch = (_options: DropdownItemProps, query: string) => {
        foundOptions = selectedItemsState ?
            stopsState
                .filter(({ directionVariantInternalId, stopId, stopName, stopSequence }) => directionVariantInternalId === selectedItemsState.directionVariantId
                    && (stopId.startsWith(query) || stopName.startsWith(query) || stopSequence === Number(query)))
                .map(({ stopId, stopName }) => ({ value: stopId, text: `${stopName} (${stopId})` }))
            : [];
        return [...new Map(foundOptions.map(item => [item['value'], item])).values()]; //remove duplicates by stopId (value);
    };
    const navigateToStop = (stopData: CoordinatesObject) => {
        setMapBoundsState(undefined);
        setZoom(17);
        setCenter([stopData.latitude, stopData.longitude]);
        setInfoboxesState(prevState => (
            prevState.map(i => i.location[0] === stopData.latitude && i.location[1] === stopData.longitude ?
                { ...i, options: { ...i.options, visible: true } } :
                { ...i, options: { ...i.options, visible: false } })
        ));
    };

    const clearStates = () => {
        setStopsState([]);
        setPushpinsState([]);
        setInfoboxesState([]);
        setPolylineState(undefined);
        setRoutesListState([]);
        setSelectedItemsState(undefined);
        setTripsState([]);
        setSelectedStopState(undefined);
        setHeadwaysState([]);
        setHeadwaysChartState([]);
        setStopsDropdownState(initialStopsState);
    };

    const tableData = headwaysState.length > 0 ? headwaysState : tripsState;

    const getTabPanes = () => {
        const tabPanes = [
            {
                menuItem: 'Routes',
                pane: (
                    <Tab.Pane key="routesTab" style={styles.tab} >
                        <div className="speedmap side" style={styles.speedMap}>
                            <Segment id="routeSelectionPanel" style={styles.routeSelection}>
                                {routesListState.length === 0 ? <Loader active>Loading active routes...</Loader> : null}
                                {routesListState.map((r, i) => (
                                    <RouteVariantsSelector key={i}
                                        route={r}
                                        onChecked={handleRouteVariantChecked}
                                    />
                                ))}
                            </Segment>
                        </div>
                    </Tab.Pane >
                ),
            },
        ];
        if (tableData.length > 0) {
            tabPanes.push({
                menuItem: 'Trips',
                pane: (
                    <Tab.Pane key="tripsTab" style={styles.tab}>
                        <label className="categoryHeader">{selectedItemsState ? `Trips for Route ${selectedItemsState.routeName} (${selectedItemsState.directionName})` : ''}</label>
                        <Dropdown
                            placeholder="Start typing name or ID of a stop"
                            search={stopsSearch}
                            selection
                            clearable
                            icon={stopsDropdownState.selectedValue ? 'delete' : undefined}
                            openOnFocus={false}
                            selectOnBlur={false}
                            options={stopsDropdownState.options}
                            value={stopsDropdownState.selectedValue}
                            onChange={handleStopChange}
                            style={styles.stopsDropdown}
                        />
                        <StopHeadwaysTable rows={tableData} />
                    </Tab.Pane >
                ),
            });
        }
        if (headwaysChartState.length > 0) {
            tabPanes.push({
                menuItem: 'Arrival Diagram',
                pane: (
                    <Tab.Pane key="chartTab" style={styles.tab}>
                        <Dropdown
                            placeholder="Start typing name or ID of a stop"
                            search={stopsSearch}
                            selection
                            clearable
                            icon={stopsDropdownState.selectedValue ? 'delete' : undefined}
                            openOnFocus={false}
                            selectOnBlur={false}
                            options={stopsDropdownState.options}
                            value={stopsDropdownState.selectedValue}
                            onChange={handleStopChange}
                            style={styles.stopsDropdown}
                        />
                        <StopHeadwaysChart chartData={headwaysChartState} />
                    </Tab.Pane >
                ),
            });
        }
        return tabPanes;
    };

    return (
        <BlockUi tag="div" blocking={formBlockingState}>
            <Form>
                <Header as="h1" className="reportHeader">
                    Headways at Stops
                </Header>
                <Form.Group widths="equal" inline className="inputGroup">
                    <Form.Field className="calendarField">
                        <DateInput
                            name="fromDateCalendar"
                            fluid
                            dateFormat="YYYY-MM-DD"
                            placeholder="Select date"
                            value={selectedDateState}
                            iconPosition="left"
                            popupPosition="bottom center"
                            closable={true}
                            animation="fade"
                            onChange={handleSelectedDateChange}
                            className="calendarInput"
                            maxDate={new Date()}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Button
                            primary
                            content="Apply"
                            onClick={retrieveTripsData}
                            className="primaryButton"
                        />
                    </Form.Field>
                </Form.Group>
                <Form.Group>
                    <Form.Field width={16}>
                        <label className="categoryHeader">Pick a route direction and click on stop sign to see headways</label>
                    </Form.Field>
                </Form.Group>
                <Form.Group className="bottomGroup" style={styles.layout}>
                    <Form.Field width={tabIndexState === 0 ? 11 : 10} style={styles.mapContainer}>
                        <TransitMap
                            map={{
                                center,
                                options: {
                                    zoom,
                                    bounds: mapBoundsState,
                                },
                            }}
                            polylines={polylineState && [polylineState]}
                            pushpins={pushpinsState}
                            infoboxes={infoboxesState}
                        />
                    </Form.Field>
                    <Form.Field width={tabIndexState === 0 ? 5 : 6} style={styles.tabContainer}>
                        <Tab menu={{ pointing: true }} panes={getTabPanes()} renderActiveOnly={false} activeIndex={tabIndexState} onTabChange={handleTabChange} />
                    </Form.Field>
                </Form.Group>
            </Form>
        </BlockUi>
    );
};

export default connect(
    (state: AppState) => ({ agency: getSelectedOrDefaultAgency(state) }),
)(StopHeadways);
